import { defineComponent, h, Ref, ref, onUnmounted } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { Icon } from '@/ui/components/icon/Icon';
import { useFillDetailModal } from '@/ui/pages/conduct/FillDetailsIssueModal/useFillDetailModal';
import { IssueLegacy } from '@/ui/pages/checklist/adaptors/IssueAdaptors';
import { cloneDeep } from 'lodash';
import { IssuePriority } from '@/entities/issue';
import { EventBus } from '@/ui/EventBus';
import { Events } from '@/ui/events';
import { DueAfter } from '@/entities/issueTemplate';
import {
  toasted,
  sequence,
  filterListToEditableGroups,
  filterListToEditableUsers,
  mapGroupIdsToOptions,
  mapUsersIdsToOptions,
} from '@/ui/helpers';
import { useTask } from '@/ui/concerns';
import { filter as getAllUsers } from '@/services/api/resources/user';
import { filter as getAllGroups } from '@/services/api/resources/group';
import { loader } from '@/ui/components';
import { useAssignees } from '@/ui/concerns/useAssignees';

interface FillFieldsProps {
  value: IssueLegacy;
}

export const FillFields = defineComponent<FillFieldsProps>({
  name: 'FillFields',
  props: ['value'],
  setup(props) {
    const internalActiveIssueEditable = ref<IssueLegacy>(cloneDeep(props.value));
    const getAssignees = () => internalActiveIssueEditable.value.params.assignees;

    if (getAssignees()?.users?.length) {
      internalActiveIssueEditable.value.params.assignees.users = mapUsersIdsToOptions(getAssignees().users);
    }

    if (getAssignees()?.groups?.length) {
      internalActiveIssueEditable.value.params.assignees.groups = mapGroupIdsToOptions(getAssignees().groups);
    }

    if (props.value.settings.priority_required) {
      internalActiveIssueEditable.value.params.priority = IssuePriority.Normal;
    }

    const {
      fields,
      showValidationErrors,
      hasNoValidationErrors,
    } = useFillDetailModal(internalActiveIssueEditable as Ref<IssueLegacy>);

    const isDueValid = () => {
      if (!internalActiveIssueEditable.value.settings.due_after_required) {
        return true;
      }

      const dueIn = internalActiveIssueEditable.value.params.due_in as DueAfter;

      if (!dueIn) {
        return false;
      }

      // @ts-ignore
      return Object.keys(dueIn).some(key => dueIn[key] !== null);
    };

    const isPhotoValid = () => {
      if (!internalActiveIssueEditable.value.settings.photos_required) {
        return true;
      }

      return internalActiveIssueEditable.value.photos && internalActiveIssueEditable.value.photos.length > 0;
    };

    const onSaveClick = () => {
      const saveValue = cloneDeep(internalActiveIssueEditable.value);
      saveValue.params.assignees.users = filterListToEditableUsers(getAssignees().users ?? []);
      saveValue.params.assignees.groups = filterListToEditableGroups(getAssignees().groups ?? []);
      showValidationErrors.value = true;

      if (!hasNoValidationErrors.value) {
        return;
      }

      if (!isDueValid()) {
        toasted.error(t('issues.due-after-required'));

        return;
      }

      if (!isPhotoValid()) {
        toasted.error(t('issues.photos-required'));

        return;
      }

      EventBus.$emit(Events.fillIssueDetailInspectionPageSave, saveValue);
    };

    const {
      assigneeOptions,
      fillGroupsOptions,
      fillAssigneeOptions,
      getUsers,
    } = useAssignees();

    const getFilters = useTask(
      () => sequence(getAllUsers(), getAllGroups()),
      ([users, groups]) => {
        fillGroupsOptions(groups);
        fillAssigneeOptions(users);
      },
    );

    EventBus.$on(Events.fillIssueDetailsInspectionPage, onSaveClick);

    onUnmounted(() => {
      EventBus.$off(Events.fillIssueDetailsInspectionPage, onSaveClick);
    });

    return function render() {
      return loader('div', getFilters, [
        h(Icon, {
          class: 'has-text-warning',
          props: {
            iconPack: 'fal',
            icon: 'info-circle',
          },
        }),
        h('span', t('issues.provide-information')),
        h('div', { class: 'mt-3' }, [
          ...fields(assigneeOptions.value, getUsers),
        ]),
      ]);
    };
  },
});
